import type { CompileContext } from "../compile/context.ts";
import type { MapDocument, PropInstance } from "../document/types.ts";
import { numberActivationTypes, typesCarried, type NumberedActivationType } from "./activation-categories.ts";
import { activationEntries, activationLook, footprintArea, type ActivationEntry, type ActivationLook } from "./activation-map.ts";
import type { ValidationIssue } from "./adapter.ts";
import type { ObjectTypeDefinition } from "./objects.ts";
import type { PresenceArea } from "./presence-area.ts";
import type { FrameRectOf } from "./stand-in-presence.ts";
import type { PropRef } from "./templates.ts";

/**
 * The map's activation view state (P1.6-T32, ADR-070): whether the Activation overlay draws every behaviour
 * prop's cells, and which prop, if any, Edit on map paints the cells of. View state: nothing here is saved,
 * compiled or undone; the cells a stroke paints are written through the store as one command each, like the
 * cell editors' strokes. Editing is refused, and let go once under way, for a prop that is gone, carries no
 * behaviour or lies on a layer the tools may not edit (locked or hidden, `isEditable`), so no stroke ever
 * reaches such a prop. It also lays the cells out for the map in the state the map shows each prop in
 * (`activationLook`, `footprintArea`, `activationEntries`), from what its store holds, and numbers the map's
 * behaviour types for the overlay's categories and its legend (`numbered`, ADR-070's amendment of 2026-09-27).
 */

/**
 * What the view state reads from its store.
 */
export interface ActivationOnMapHost {
  readonly propOf: (layerId: string, propId: string) => PropInstance | undefined;
  readonly isEditable: (layerId: string) => boolean;
  readonly notify: () => void;
  readonly map: () => MapDocument;
  readonly context: () => CompileContext;
  readonly tileSize: () => number;
  readonly shownState: (layerId: string, prop: PropInstance) => string | null;
  readonly issues: () => readonly ValidationIssue[];
  readonly shown: (layerId: string) => boolean;
  readonly types: () => readonly ObjectTypeDefinition[];
  readonly onStart?: () => void;
}

/**
 * The overlay's toggle and the prop being edited on the map.
 */
export class ActivationOnMap {
  private overlayShown = false;
  private edited: PropRef | null = null;
  private numberedFor: { readonly map: MapDocument; readonly types: readonly ObjectTypeDefinition[]; readonly numbered: readonly NumberedActivationType[] } | null = null;

  /**
   * @param host The store's reads and its notification.
   */
  public constructor(private readonly host: ActivationOnMapHost) {}

  /**
   * Whether the Activation overlay is shown.
   *
   * @returns True while shown.
   */
  public get overlay(): boolean {
    return this.overlayShown;
  }

  /**
   * Shows or hides the Activation overlay, notifying only on a change.
   *
   * @param shown Whether shown.
   */
  public setOverlay(shown: boolean): void {
    if (shown === this.overlayShown) {
      return;
    }
    this.overlayShown = shown;
    this.host.notify();
  }

  /**
   * The prop whose cells Edit on map paints.
   *
   * @returns The prop, or null while nothing is edited on the map.
   */
  public get editing(): PropRef | null {
    return this.edited;
  }

  /**
   * Whether Edit on map paints a prop's cells.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @returns True while that prop is edited on the map.
   */
  public isEditing(layerId: string, propId: string): boolean {
    return this.edited !== null && this.edited.layerId === layerId && this.edited.propId === propId;
  }

  /**
   * Starts Edit on map for a prop.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @returns True when editing started, false when it was refused.
   */
  public startEditing(layerId: string, propId: string): boolean {
    if (!this.editable(layerId, propId)) {
      return false;
    }
    if (!this.isEditing(layerId, propId)) {
      this.host.onStart?.();
      this.edited = { layerId, propId };
      this.host.notify();
    }
    return true;
  }

  /**
   * Ends Edit on map, notifying only when it was under way.
   */
  public endEditing(): void {
    if (this.edited === null) {
      return;
    }
    this.edited = null;
    this.host.notify();
  }

  /**
   * Lets editing go once its prop can no longer be edited, without notifying: the store calls it on the way to
   * notifying its subscribers.
   */
  public release(): void {
    if (this.edited !== null && !this.editable(this.edited.layerId, this.edited.propId)) {
      this.edited = null;
    }
  }

  /**
   * A prop's activation cells on the map in the state the map shows it in.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @param rectOf The asset manifest's frame rects.
   * @returns The look, or null for a missing prop or one `activationLook` cannot lay out.
   */
  public look(layerId: string, propId: string, rectOf: FrameRectOf): ActivationLook | null {
    const prop = this.host.propOf(layerId, propId);
    return prop === undefined ? null : activationLook(prop, this.host.shownState(layerId, prop), this.host.context(), rectOf, this.host.tileSize());
  }

  /**
   * The footprint a prop blocks on the map in the state the map shows it in.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @returns The world collision cells, or null for a missing prop.
   */
  public footprint(layerId: string, propId: string): PresenceArea | null {
    const prop = this.host.propOf(layerId, propId);
    return prop === undefined ? null : footprintArea(prop, this.host.shownState(layerId, prop), this.host.context(), this.host.tileSize());
  }

  /**
   * The Activation overlay's props: every behaviour prop of a shown layer in the state the map shows it in,
   * with its worst issue.
   *
   * @param rectOf The asset manifest's frame rects.
   * @returns The entries.
   */
  public entries(rectOf: FrameRectOf): ActivationEntry[] {
    const { host } = this;
    return activationEntries({ map: host.map(), context: host.context(), rectOf, tileSize: host.tileSize(), shownState: host.shownState, issues: host.issues(), shown: host.shown });
  }

  /**
   * The map's behaviour types numbered for the overlay's categories and its legend, read again only when the
   * map or the registered types change, so the map, the legend and the page's report share one numbering.
   *
   * @returns The numbered types in number order.
   */
  public numbered(): readonly NumberedActivationType[] {
    const map = this.host.map();
    const types = this.host.types();
    const kept = this.numberedFor;
    if (kept !== null && kept.map === map && kept.types === types) {
      return kept.numbered;
    }
    const numbered = numberActivationTypes(types, typesCarried(map));
    this.numberedFor = { map, types, numbered };
    return numbered;
  }

  /**
   * Whether a prop's cells may be edited on the map.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @returns True for a behaviour prop on an editable layer.
   */
  private editable(layerId: string, propId: string): boolean {
    return this.host.isEditable(layerId) && this.host.propOf(layerId, propId)?.behaviour !== undefined;
  }
}
